import React, { useEffect, useMemo, useState } from 'react';
import { TextField, Button, MenuItem, Select, InputLabel, FormControl, Grid, SelectChangeEvent } from '@mui/material';
import { useLimits } from '../utils/limitsContext';

interface InputFormProps {
  onCalculate: (data: {
    monthlySalary: number;
    additionalMonths: number;
    bonusMonths: number;
    monthlySubsidy: number;
    additionalTaxFree?: number;
    housingFundRate: number;
    province: string;
    city: string;
  }) => void;
}

const InputForm: React.FC<InputFormProps> = ({ onCalculate }) => {
  const { limits } = useLimits();

  const provinces = useMemo(() => Object.keys(limits.provinceCityMapping ?? {}), [limits]);
  const defaultProvince = provinces[0] ?? '';

  const [monthlySalary, setMonthlySalary] = useState<string>('');
  const [additionalMonths, setAdditionalMonths] = useState<string>('0');
  const [bonusMonths, setBonusMonths] = useState<string>('0');
  const [monthlySubsidy, setMonthlySubsidy] = useState<string>('');
  const [additionalTaxFree, setAdditionalTaxFree] = useState<string>('');
  const [housingFundRate, setHousingFundRate] = useState<string>('9');

  const [province, setProvince] = useState<string>(defaultProvince);
  const citiesForProvince = useMemo(
    () => (province ? limits.provinceCityMapping?.[province] ?? [] : []),
    [limits, province]
  );
  const defaultCity = citiesForProvince[0] ?? '';
  const [city, setCity] = useState<string>(defaultCity);

  useEffect(() => {
    if (!province) {
      if (defaultProvince) setProvince(defaultProvince);
      return;
    }
    if (citiesForProvince.length === 0) {
      if (city !== '') setCity('');
      return;
    }
    if (!citiesForProvince.includes(city)) {
      setCity(citiesForProvince[0]);
    }
  }, [province, defaultProvince, citiesForProvince, city]);

  const handleProvinceChange = (e: SelectChangeEvent<string>) => {
    const selectedProvince = e.target.value;
    setProvince(selectedProvince);
    const nextCity = (limits.provinceCityMapping?.[selectedProvince] ?? [])[0] ?? '';
    setCity(nextCity);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCalculate({
      monthlySalary: parseFloat(monthlySalary) || 0,
      additionalMonths: parseFloat(additionalMonths) || 0,
      bonusMonths: parseFloat(bonusMonths) || 0,
      monthlySubsidy: parseFloat(monthlySubsidy) || 0,
      additionalTaxFree: parseFloat(additionalTaxFree) || 0,
      housingFundRate: parseFloat(housingFundRate) / 100 || 0,
      province: province || defaultProvince || '',
      city: city || defaultCity || '',
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Monthly Base Salary"
            type="number"
            value={monthlySalary}
            onChange={(e) => setMonthlySalary(e.target.value)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Monthly Subsidy"
            type="number"
            value={monthlySubsidy}
            onChange={(e) => setMonthlySubsidy(e.target.value)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Additional Months of Base Salary"
            type="number"
            value={additionalMonths}
            onChange={(e) => setAdditionalMonths(e.target.value)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Bonus Months"
            type="number"
            value={bonusMonths}
            onChange={(e) => setBonusMonths(e.target.value)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Additional Tax-Free Amount (Monthly)"
            type="number"
            value={additionalTaxFree}
            onChange={(e) => setAdditionalTaxFree(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Housing Fund Rate (%)"
            type="number"
            value={housingFundRate}
            onChange={(e) => setHousingFundRate(e.target.value)}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Province</InputLabel>
            <Select value={province} onChange={handleProvinceChange}>
              {provinces.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>City</InputLabel>
            <Select value={city} onChange={(e) => setCity(e.target.value as string)}>
              {citiesForProvince.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Button type="submit" variant="contained" color="primary" fullWidth>
            Calculate
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default InputForm;